import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import logoImage from "../../assets/images/logoNew.jpg";
import UseTitle from "../../Utility/UseTitle/UseTitle";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import { ROUTES } from "../../constants";
import { verifyEmail } from "../../Redux/Thunks/usersThunk";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState("");

  const handleVerifyClick = () => {
    if (!token) {
      setStatus("error");
      setErrorMessage("Invalid or missing verification link.");
      return;
    }
    setStatus("loading");
    setErrorMessage("");
    dispatch(verifyEmail(token))
      .unwrap()
      .then(() => {
        setStatus("success");
        setTimeout(() => navigate(ROUTES.VERIFY_EMAIL_SUCCESS), 800);
      })
      .catch((err) => {
        setStatus("error");
        setErrorMessage(typeof err === "string" ? err : err?.message || "Verification failed.");
      });
  };

  return (
    <div>
      <UseTitle formattedTitle="Verify Your Email" />
      <div className="auth-page-wrapper">
        <div className="auth-page-content pb-0">
          <div className="w-full max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center h-screen">
              <div className="w-full md:w-2/3 lg:w-1/2 xl:w-2/5">
                <div className="bg-surface-primary rounded-md border border-border-primary shadow-sm mt-4">
                  <div className="mt-5">
                    <div className="flex justify-center items-center">
                      <img
                        src={logoImage}
                        alt="Logo"
                        className="h-[72px] w-auto max-w-[280px] object-contain"
                      />
                    </div>
                  </div>
                  <div className="p-4 mt-4">
                    <div className="p-2">
                      <Typography variant="h4" className="text-center mb-4 text-theme-primary">
                        Verify Your Email
                      </Typography>

                      {status === "loading" && (
                        <div className="text-center py-6">
                          <div className="inline-block h-10 w-10 border-2 border-theme-primary border-t-transparent rounded-full animate-spin" />
                          <Typography variant="text2" className="mt-3 text-text-secondary">
                            Verifying your email...
                          </Typography>
                        </div>
                      )}

                      {status === "success" && (
                        <Typography variant="text2" className="text-center mb-6 text-badge-success">
                          Email verified successfully. Redirecting...
                        </Typography>
                      )}

                      {status === "error" && (
                        <>
                          <Typography
                            variant="text2"
                            className="text-center mb-4 text-badge-danger"
                          >
                            {errorMessage}
                          </Typography>
                          <div className="mt-4 text-center">
                            <Link to={ROUTES.LOGIN}>
                              <Button type="button" variant="primary" className="w-full">
                                Back to Login
                              </Button>
                            </Link>
                          </div>
                        </>
                      )}

                      {status === "idle" && (
                        <>
                          <Typography
                            variant="text2"
                            className="text-center mb-6 text-text-secondary"
                          >
                            {token
                              ? "We've sent a verification link to your email address. Please click the button below to verify your email."
                              : "Use the verification link from your email to verify your account. If you followed the link from the email, click Verify Email below."}
                          </Typography>
                          <div className="mt-4">
                            <Button
                              type="button"
                              variant="primary"
                              onClick={handleVerifyClick}
                              disabled={!token}
                              className="w-full flex items-center justify-center gap-2"
                            >
                              <Typography variant="text2" component="span">
                                Verify Email
                              </Typography>
                            </Button>
                          </div>
                          <div className="mt-4 text-center">
                            <Link
                              to={ROUTES.LOGIN}
                              className="text-text-secondary hover:text-theme-primary transition-colors text-sm"
                            >
                              Back to Login
                            </Link>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
